/* Copper Studio — cart drawer upsell ("Add more, save more"). ES module, loaded from layout/theme.liquid. */
import { CartAddEvent } from '@theme/events';

const sectionIds = () =>
  [...document.querySelectorAll('cart-items-component')]
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
    .filter(Boolean);

async function addUpsell(button) {
  const variantId = Number(button.dataset.csUpsellAdd);
  if (!variantId || button.disabled) return;
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  try {
    const ids = sectionIds();
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: ids.join(',') }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data?.description || 'Could not add item');
    document.dispatchEvent(
      new CartAddEvent({}, 'cs-cart-upsell', {
        source: 'cs-cart-upsell',
        itemCount: 1,
        variantId: String(variantId),
        sections: data.sections,
      })
    );
  } catch (error) {
    console.warn(error);
    button.disabled = false;
  } finally {
    button.removeAttribute('aria-busy');
  }
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const add = target.closest('[data-cs-upsell-add]');
  if (add instanceof HTMLButtonElement) {
    event.preventDefault();
    addUpsell(add);
    return;
  }

  const arrow = target.closest('[data-cs-upsell-dir]');
  if (arrow instanceof HTMLElement) {
    const track = arrow.closest('.cs-upsell, .cs-buymore')?.querySelector('[data-cs-upsell-track]');
    if (track) track.scrollBy({ left: Number(arrow.dataset.csUpsellDir) * track.clientWidth, behavior: 'smooth' });
  }
});

/* "Buy more, Save more" on product pages: replace the collection cards with Shopify's related-product
   recommendations for the product being viewed, topped up from the collection, in pages of N cards. */
const money = (cents) =>
  new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
    style: 'currency',
    currency: window.Shopify?.currency?.active || 'GBP',
  }).format(cents / 100);

const escapeHtml = (text) =>
  String(text).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

function buyMoreCard(product) {
  const variant = product.variants.find((v) => v.available) || product.variants[0];
  const onSale = variant.compare_at_price > variant.price;
  const image = product.featured_image
    ? `<img src="${product.featured_image}${product.featured_image.includes('?') ? '&' : '?'}width=140" width="64" height="64" loading="lazy" alt="">`
    : '';
  const li = document.createElement('li');
  li.className = 'cs-buymore__item';
  li.dataset.productId = product.id;
  li.innerHTML = `
    <a class="cs-buymore__media" href="${product.url}" tabindex="-1">${image}</a>
    <div class="cs-buymore__info">
      <a class="cs-buymore__name" href="${product.url}">${escapeHtml(product.title)}</a>
      <span class="cs-buymore__price">
        <span class="${onSale ? 'cs-buymore__sale' : ''}">${money(variant.price)}</span>${onSale ? ` <s>${money(variant.compare_at_price)}</s>` : ''}
      </span>
    </div>
    <button type="button" class="cs-buymore__add" data-cs-upsell-add="${variant.id}">
      <svg viewBox="0 0 12 12" width="10" height="10" aria-hidden="true"><path d="M6 1v10M1 6h10" stroke="currentColor" stroke-width="1.5"/></svg>
      Add
    </button>`;
  return li;
}

async function loadRelatedBuyMore(box) {
  const { productId, limit, perPage, hideCurrent } = box.dataset;
  const track = box.querySelector('[data-cs-upsell-track]');
  if (!track) return;
  const max = Number(limit) || 9;
  const size = Number(perPage) || 3;
  try {
    const response = await fetch(
      `${window.Shopify?.routes?.root || '/'}recommendations/products.json?product_id=${productId}&limit=${max}&intent=related`
    );
    if (!response.ok) return;
    const { products = [] } = await response.json();
    const cards = products.filter((p) => p.available && String(p.id) !== productId).map(buyMoreCard);
    const seen = new Set(cards.map((c) => c.dataset.productId));
    for (const item of track.querySelectorAll('.cs-buymore__item')) {
      if (cards.length >= max) break;
      const id = item.dataset.productId;
      if (seen.has(id) || (hideCurrent === 'true' && id === productId)) continue;
      seen.add(id);
      cards.push(item.cloneNode(true));
    }
    if (!cards.length) return;
    const pages = [];
    for (let i = 0; i < Math.min(cards.length, max); i += size) {
      const page = document.createElement('li');
      page.className = 'cs-buymore__page';
      const list = document.createElement('ul');
      list.className = 'cs-buymore__cards';
      list.setAttribute('role', 'list');
      list.append(...cards.slice(i, i + size));
      page.append(list);
      pages.push(page);
    }
    track.replaceChildren(...pages);
    track.scrollLeft = 0;
  } catch (error) {
    console.warn('Related products could not be loaded', error);
  }
}

for (const box of document.querySelectorAll('[data-cs-buymore-related]')) loadRelatedBuyMore(box);
